import { useState, useEffect } from 'react';
import { Comment } from '../types';
import { useForum } from '../useForum';
import { MarkdownContent } from './MarkdownContent';
import { CommentForm } from './CommentForm';
import { VoteButton } from './VoteButton';
import { TimeAgo } from './TimeAgo';
import { User } from 'lucide-react';
import { CommentSkeleton } from './CommentSkeleton';
import { cn } from '@/utils';
import { formatInstantInTimezone, getDisplayTimezone } from '@/utils/timezone';

interface CommentTreeProps {
  comments: Comment[];
  depth?: number;
  maxDepth?: number;
}

const MAX_DEPTH = 10;
const INDENT_PX = 28;

export function CommentTree({ comments, depth = 0, maxDepth = MAX_DEPTH }: CommentTreeProps) {
  const { voteOnComment, getUserVote } = useForum();

  if (comments.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {comments.map((comment) => {
        const userVote = getUserVote(comment.id, 'comment');
        const isNested = depth > 0;

        return (
          <div
            key={comment.id}
            className={cn(
              "group relative",
              isNested && "ml-6 pl-4 border-l-2 border-border/50 hover:border-primary/30 transition-colors"
            )}
          >
            <div className="flex gap-4">
              <div className="flex-shrink-0 pt-0.5">
                <VoteButton
                  upvotes={comment.upvotes}
                  downvotes={comment.downvotes}
                  userVote={userVote}
                  onVote={(voteType) => voteOnComment(comment.id, voteType).catch(console.error)}
                  compact
                />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-medium text-foreground text-xs">{comment.author}</span>
                  <span className="text-xs text-foreground/40">·</span>
                  <TimeAgo timestamp={comment.createdAt} />
                  {comment.updatedAt !== comment.createdAt && (
                    <>
                      <span className="text-xs text-foreground/40">·</span>
                      <span className="text-xs text-foreground/50">(edited)</span>
                    </>
                  )}
                </div>

                <div className="text-sm text-foreground/90 leading-relaxed mb-3">
                  <MarkdownContent content={comment.content} />
                </div>

                {comment.children && comment.children.length > 0 && depth < maxDepth && (
                  <div className="mt-4">
                    <CommentTree comments={comment.children} depth={depth + 1} maxDepth={maxDepth} />
                  </div>
                )}

                {depth >= maxDepth && comment.children && comment.children.length > 0 && (
                  <div className="mt-3 text-xs text-muted-foreground italic">
                    (Further nested comments hidden)
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface CommentSectionProps {
  postId: string;
}

export function CommentSection({ postId }: CommentSectionProps) {
  const { getCommentsByPost, fetchComments, state } = useForum();
  const comments = getCommentsByPost(postId);
  const [refreshKey, setRefreshKey] = useState(0);
  const isLoading = state.loading.comments.has(postId);

  useEffect(() => {
    fetchComments(postId);
  }, [postId, fetchComments]);

  const handleCommentSuccess = () => {
    fetchComments(postId);
    setRefreshKey(prev => prev + 1);
  };

  const formatCommentDate = (ts: string) => {
    const d = new Date(ts);
    const diffMs = Date.now() - d.getTime();
    if (diffMs < 60000) return 'JUST NOW';
    const mins = Math.floor(diffMs / 60000);
    if (mins < 60) return `${mins} ${mins === 1 ? 'MIN' : 'MINS'} AGO`;
    const hrs = Math.floor(diffMs / 3600000);
    if (hrs < 24) return `${hrs} ${hrs === 1 ? 'HOUR' : 'HOURS'} AGO`;
    return formatInstantInTimezone(
      d,
      getDisplayTimezone(),
      { month: 'short', day: 'numeric', year: 'numeric' },
      'en-US'
    ).toUpperCase();
  };

  const flattenComments = (items: Comment[]): Comment[] =>
    items.flatMap((c) => [c, ...flattenComments(c.children || [])]);

  const allComments = flattenComments(comments);
  const totalCount = allComments.length;

  // Replies nest inside their parent's card, so every level used to cost an
  // indent, an avatar, a gap and the card's padding, about 100px: on a 360px
  // phone a first-level reply had about 130px for its text and a second-level
  // one about 30px. Below 640px a reply drops its avatar and indent and the
  // card padding tightens, so a level costs about 36px (T101026002).
  const renderComment = (comment: Comment, isNested = false) => (
    <div key={comment.id} className={`flex gap-3 sm:gap-4 group ${isNested ? 'mt-4 sm:ml-4' : ''}`}>
      <div
        className={`w-9 h-9 rounded-full bg-muted border border-border items-center justify-center flex-shrink-0 mt-0.5 ${isNested ? 'hidden sm:flex' : 'flex'}`}
      >
        <User size={16} className="text-muted-foreground" />
      </div>
      <div className="min-w-0 flex-1 bg-muted/30 rounded-2xl p-3 sm:p-4">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mb-2">
          <div>
            <span className="text-sm font-bold text-foreground">{comment.author}</span>
            <span className="text-[11px] text-muted-foreground ml-2">Community Member</span>
          </div>
          <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-widest">
            {formatCommentDate(comment.createdAt)}
          </span>
        </div>
        <div className="text-sm text-foreground/90 leading-relaxed">
          <MarkdownContent content={comment.content} />
        </div>
        {comment.children && comment.children.length > 0 && (
          <div className="mt-4 space-y-4">
            {comment.children.map((child) => renderComment(child, true))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {isLoading && comments.length === 0 ? (
        <CommentSkeleton />
      ) : comments.length === 0 ? (
        <section className="bg-muted/30 rounded-2xl p-6 md:p-8">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-5">Leave a reply</h3>
          <CommentForm postId={postId} onSuccess={handleCommentSuccess} />
        </section>
      ) : (
        <>
          <section className="space-y-8 mb-12">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
              {totalCount} {totalCount === 1 ? 'Response' : 'Responses'}
            </h3>
            <div className="space-y-8">
              {comments.map((c) => renderComment(c))}
            </div>
          </section>
          <section className="bg-muted/30 rounded-2xl p-6 md:p-8">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-5">Leave a reply</h3>
            <CommentForm postId={postId} onSuccess={handleCommentSuccess} />
          </section>
        </>
      )}
    </>
  );
}

